/**
 * SlackLinkPage — landing page for the link-token URL posted to unlinked
 * Slack users by the session dispatcher.
 *
 * URL contract: /slack/link?token=<signed token>
 *
 * Flow:
 *   1. Read token from query string. Missing → show error.
 *   2. Check auth session. Unauthenticated → show sign-in prompt that returns
 *      here after auth.
 *   3. Authenticated → POST /api/slack/link-identity with the token.
 *   4. Render outcome: linked / already-linked / invalid-or-expired / error.
 *
 * No app chrome — this is a standalone page reachable from outside the
 * authenticated SPA shell.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AuthSessionResponse } from "@yagni/shared";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";

type Status =
  | "loading"
  | "missing_token"
  | "needs_auth"
  | "linking"
  | "linked"
  | "already_redeemed"
  | "invalid_token"
  | "error";

function buildGoogleAuthUrl(returnTo: string): string {
  const params = new URLSearchParams({ returnTo });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone) params.set("timezone", timezone);
  return `/api/auth/google?${params.toString()}`;
}

export function SlackLinkPage() {
  // Read token directly from the URL — useSearchParams would force a full
  // react-router context, but this page renders standalone outside the App
  // shell, so we lean on window.location like OAuthPopupComplete does.
  const token = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("token");
  }, []);

  const returnTo = useMemo(() => {
    if (typeof window === "undefined") return "/slack/link";
    return `${window.location.pathname}${window.location.search}`;
  }, []);

  const [status, setStatus] = useState<Status>("loading");
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);

  const redeem = useCallback(async () => {
    if (!token) return;
    setStatus("linking");
    try {
      const res = await fetch("/api/slack/link-identity", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
        credentials: "same-origin",
      });
      if (res.ok) {
        setStatus("linked");
        return;
      }
      if (res.status === 401) {
        setStatus("needs_auth");
        return;
      }
      if (res.status === 410) {
        setStatus("already_redeemed");
        return;
      }
      if (res.status === 400) {
        setStatus("invalid_token");
        return;
      }
      setStatus("error");
    } catch {
      setStatus("error");
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setStatus("missing_token");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/auth/session", { credentials: "same-origin" });
        if (cancelled) return;
        if (!res.ok) {
          setStatus("needs_auth");
          return;
        }
        const session = (await res.json()) as AuthSessionResponse;
        setSignedInEmail(session.user.email);
        await redeem();
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, redeem]);

  return (
    <div className="min-h-screen [background:var(--canvas-field)] flex items-center justify-center px-4 py-8">
      {/* Confirmation card: print. The link is a record being written, not a
          decision waiting on the human. */}
      <Card
        variant="default"
        padding="none"
        className="w-full max-w-lg rounded-[var(--radius-pane)]"
      >
        <div className="border-b border-[var(--border-subtle)] px-6 py-5">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
            Link your Slack account
          </h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Connect this Slack identity to your YAGNI account so @yagni can
            read your messages and post on your behalf.
          </p>
        </div>

        <div className="space-y-4 px-6 py-5" data-testid="slack-link-status">
          {status === "loading" || status === "linking" ? (
            <div className="text-sm text-[var(--text-secondary)]">
              {status === "loading" ? "Loading..." : "Linking your account..."}
            </div>
          ) : null}

          {status === "missing_token" ? (
            <Card tone="error" padding="sm" className="rounded-xl text-sm text-error">
              The link is missing a token. Open the link from the most recent
              message YAGNI sent you in Slack — older links expire after 24
              hours.
            </Card>
          ) : null}

          {status === "needs_auth" ? (
            <>
              <p className="text-sm text-[var(--text-secondary)]">
                Sign in to your YAGNI account to finish linking. We'll bring you
                back here automatically.
              </p>
              <Button
                variant="primary"
                onClick={() => window.location.assign(buildGoogleAuthUrl(returnTo))}
              >
                Continue with Google
              </Button>
            </>
          ) : null}

          {status === "linked" ? (
            <Card variant="outlined" padding="md" className="rounded-xl">
              <div className="text-sm text-[var(--text-secondary)]">Linked as</div>
              <div className="mt-1 text-lg font-medium text-[var(--text-primary)]">
                {signedInEmail ?? "your account"}
              </div>
              <p className="mt-3 text-sm text-[var(--text-secondary)]">
                You can close this tab and return to Slack — @yagni will reply
                in the next thread.
              </p>
            </Card>
          ) : null}

          {status === "already_redeemed" ? (
            <Card variant="outlined" padding="md" className="rounded-xl">
              <div className="text-sm text-[var(--text-primary)]">
                This link has already been used.
              </div>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                If you didn't link your account yourself, ask @yagni in Slack
                for a fresh link — links are single-use for security.
              </p>
            </Card>
          ) : null}

          {status === "invalid_token" ? (
            <Card tone="error" padding="sm" className="rounded-xl text-sm text-error">
              This link is invalid or has expired. Ask @yagni in Slack for a
              fresh link — links last 24 hours.
            </Card>
          ) : null}

          {status === "error" ? (
            <Card tone="error" padding="sm" className="rounded-xl text-sm text-error">
              Something went wrong while linking your account. Try refreshing,
              or ask @yagni in Slack for a fresh link.
            </Card>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
